/**
 * Data Cleanup Page
 * =================
 * Spreadsheet-like bulk data cleanup for contractors, parks, clients, and jobs.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Save, Zap, Download, Trash2, Filter, Search,
  Building, Users, Briefcase, FileText, CheckCircle2, AlertTriangle,
  ChevronDown, Edit3, X, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const RECORD_TYPES = [
  { key: "contractors", label: "Contractors", icon: Users, color: "bg-blue-100 text-blue-700" },
  { key: "parks", label: "Parks / Sites", icon: Building, color: "bg-green-100 text-green-700" },
  { key: "clients", label: "Clients", icon: Briefcase, color: "bg-purple-100 text-purple-700" },
  { key: "jobs", label: "Jobs", icon: FileText, color: "bg-amber-100 text-amber-700" }
];

const TRAFFIC_COLORS = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  green: "bg-green-100 text-green-700 border-green-200"
};

// Editable Cell Component
const EditableCell = ({ value, field, onChange, isEditing, onStartEdit, onStopEdit }) => {
  const [localValue, setLocalValue] = useState(value || "");
  
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);
  
  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
    onStopEdit();
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setLocalValue(value || "");
      onStopEdit();
    }
  };
  
  if (isEditing) {
    if (field.type === "select") {
      return (
        <select
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            onChange(e.target.value);
            onStopEdit();
          }}
          onBlur={handleBlur}
          autoFocus
          className="w-full px-2 py-1 text-sm border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="">Select...</option>
          {field.options?.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    
    if (field.type === "tags") {
      return (
        <input
          type="text"
          value={Array.isArray(localValue) ? localValue.join(", ") : localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Comma separated..."
          className="w-full px-2 py-1 text-sm border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      );
    }
    
    return (
      <input
        type={field.type || "text"}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        className="w-full px-2 py-1 text-sm border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
    );
  }
  
  // Display mode
  const displayValue = Array.isArray(value) ? value.join(", ") : value;
  
  return (
    <div 
      onClick={onStartEdit}
      className="px-2 py-1 min-h-[28px] cursor-pointer hover:bg-slate-100 rounded text-sm truncate"
      title={displayValue || "Click to edit"}
    >
      {displayValue || <span className="text-slate-400 italic">Empty</span>}
    </div>
  );
};

export default function DataCleanupPage() {
  const navigate = useNavigate();
  
  // State
  const [recordType, setRecordType] = useState("contractors");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyingFixes, setApplyingFixes] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyIncomplete, setOnlyIncomplete] = useState(true);
  const [summary, setSummary] = useState({});
  const [editingCell, setEditingCell] = useState(null); // {rowId, field}
  const [dirtyRows, setDirtyRows] = useState({}); // {rowId: {field: newValue}}
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Load records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        record_type: recordType,
        only_incomplete: onlyIncomplete,
        page_size: "100"
      });
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/api/admin/cleanup/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        setRows(res.data.rows || []);
        setColumns(res.data.columns || []);
        setSummary(res.data.issue_summary || {});
      }
    } catch (err) {
      console.error("Failed to load records:", err);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [recordType, onlyIncomplete, search]);
  
  useEffect(() => {
    loadRecords();
    setDirtyRows({});
    setSelectedRows(new Set());
  }, [loadRecords]);
  
  // Handle cell edit
  const handleCellChange = (rowId, fieldName, newValue) => {
    setDirtyRows(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [fieldName]: newValue
      }
    }));
    
    // Update local row data
    setRows(prev => prev.map(row => {
      if (row.record_id === rowId) {
        return {
          ...row,
          editable_fields: {
            ...row.editable_fields,
            [fieldName]: newValue
          }
        };
      }
      return row;
    }));
  };
  
  // Save changes
  const saveChanges = async () => {
    const dirtyRowIds = Object.keys(dirtyRows);
    if (dirtyRowIds.length === 0) {
      toast.info("No changes to save");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const updates = dirtyRowIds.map(rowId => ({
        record_id: rowId,
        fields: dirtyRows[rowId]
      }));
      
      const res = await axios.post(`${API}/api/admin/cleanup/bulk-update`, {
        record_type: recordType,
        updates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(`Saved ${res.data.success} records`);
        setDirtyRows({});
        loadRecords();
      } else {
        toast.error(res.data.error || "Save failed");
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };
  
  // Apply autofixes
  const applyAutofixes = async (scope = "all") => {
    setApplyingFixes(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        scope,
        record_type: recordType,
        record_ids: scope === "selected" ? Array.from(selectedRows) : []
      };
      
      const res = await axios.post(`${API}/api/admin/cleanup/apply-autofix`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(`Fixed ${res.data.fixed} records`);
        loadRecords();
      } else {
        toast.error(res.data.error || "Autofix failed");
      }
    } catch (err) {
      console.error("Autofix failed:", err);
      toast.error("Failed to apply fixes");
    } finally {
      setApplyingFixes(false);
    }
  };
  
  // Toggle row selection
  const toggleRowSelection = (rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };
  
  // Select all
  const selectAll = () => {
    if (selectedRows.size === rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(rows.map(r => r.record_id)));
    }
  };
  
  const hasDirtyRows = Object.keys(dirtyRows).length > 0;
  const autoFixableCount = rows.filter(r => r.auto_fixable).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="data-cleanup-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Data Cleanup</h1>
              <p className="text-sm text-slate-500">Bulk edit and fix data quality issues</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate("/admin/test-job-purge")}
                variant="outline"
                size="sm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Test Job Purge
              </Button>
              <Button
                onClick={() => navigate("/readiness")}
                variant="outline"
                size="sm"
              >
                Readiness Audit
              </Button>
            </div>
          </div>
          
          {/* Record Type Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {RECORD_TYPES.map(type => {
              const Icon = type.icon;
              const isActive = recordType === type.key;
              return (
                <button
                  key={type.key}
                  onClick={() => setRecordType(type.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive 
                      ? "bg-teal-600 text-white shadow-sm" 
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {type.label}
                </button>
              );
            })}
          </div>
          
          {/* Filters & Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadRecords()}
                className="pl-9"
              />
            </div>
            
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyIncomplete}
                onChange={(e) => setOnlyIncomplete(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-slate-600">Only incomplete</span>
            </label>
            
            <div className="flex items-center gap-2 ml-auto">
              <Button onClick={loadRecords} variant="outline" size="sm" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              
              {autoFixableCount > 0 && (
                <Button 
                  onClick={() => applyAutofixes("all")} 
                  variant="outline" 
                  size="sm"
                  disabled={applyingFixes}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {applyingFixes ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Auto-Fix ({autoFixableCount})
                </Button>
              )}
              
              <Button 
                onClick={saveChanges} 
                disabled={!hasDirtyRows || saving}
                className="bg-teal-600 hover:bg-teal-700"
                size="sm"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
          
          {/* Summary */}
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="text-slate-500">{rows.length} records</span>
            {summary.red > 0 && (
              <Badge className={TRAFFIC_COLORS.red}>{summary.red} critical</Badge>
            )}
            {summary.amber > 0 && (
              <Badge className={TRAFFIC_COLORS.amber}>{summary.amber} warnings</Badge>
            )}
            {summary.green > 0 && (
              <Badge className={TRAFFIC_COLORS.green}>{summary.green} complete</Badge>
            )}
            {hasDirtyRows && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                {Object.keys(dirtyRows).length} unsaved changes
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Grid */}
      <div className="max-w-[1800px] mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800">All Clean!</h3>
            <p className="text-slate-500">No records need attention.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedRows.size === rows.length}
                        onChange={selectAll}
                        className="rounded border-slate-300"
                      />
                    </th>
                    <th className="p-3 text-left font-medium text-slate-600">Name</th>
                    <th className="p-3 text-left font-medium text-slate-600 w-20">Status</th>
                    {columns.map(col => (
                      <th key={col.name} className="p-3 text-left font-medium text-slate-600 min-w-[120px]">
                        {col.label}
                      </th>
                    ))}
                    <th className="p-3 text-left font-medium text-slate-600 w-32">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const isDirty = !!dirtyRows[row.record_id];
                    const isSelected = selectedRows.has(row.record_id);
                    
                    return (
                      <tr 
                        key={row.record_id}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${
                          isDirty ? "bg-blue-50" : ""
                        } ${isSelected ? "bg-teal-50" : ""}`}
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelection(row.record_id)}
                            className="rounded border-slate-300"
                          />
                        </td>
                        <td className="p-3 font-medium text-slate-800">
                          {row.display_name}
                        </td>
                        <td className="p-3">
                          <Badge className={TRAFFIC_COLORS[row.traffic] || ""}>
                            {row.traffic?.toUpperCase()}
                          </Badge>
                        </td>
                        {columns.map(col => (
                          <td key={col.name} className="p-1">
                            <EditableCell
                              value={row.editable_fields[col.name]}
                              field={col}
                              isEditing={editingCell?.rowId === row.record_id && editingCell?.field === col.name}
                              onStartEdit={() => setEditingCell({ rowId: row.record_id, field: col.name })}
                              onStopEdit={() => setEditingCell(null)}
                              onChange={(val) => handleCellChange(row.record_id, col.name, val)}
                            />
                          </td>
                        ))}
                        <td className="p-3">
                          {row.detected_issues?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {row.detected_issues.slice(0, 2).map((issue, i) => (
                                <span key={i} className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                                  {issue.field}
                                </span>
                              ))}
                              {row.detected_issues.length > 2 && (
                                <span className="text-xs text-slate-500">
                                  +{row.detected_issues.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
